import { forwardRef, type HTMLAttributes } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/cn";
import type { CardProps } from "./Card.types";

const cardVariants = cva("bfrs-rounded-xl bfrs-transition", {
  variants: {
    variant: {
      default: "bfrs-faded-card-border bfrs-bg-[image:var(--bfrs-gradient-card)] bfrs-shadow-card",
      elevated: "bfrs-faded-card-border bfrs-bg-[image:var(--bfrs-gradient-card)] bfrs-shadow-elevated",
      bordered: "bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-none",
      muted: "bfrs-border bfrs-border-border bfrs-bg-surface-muted bfrs-shadow-none",
      soft: "bfrs-border bfrs-border-primary-soft bfrs-bg-primary-soft/60 bfrs-shadow-none"
    },
    padding: {
      none: "bfrs-p-0",
      sm: "bfrs-p-3",
      md: "bfrs-p-4",
      lg: "bfrs-p-5"
    },
    interactive: {
      true: "bfrs-cursor-pointer hover:bfrs-bg-surface-muted/70 focus-within:bfrs-ring-2 focus-within:bfrs-ring-border-focus/20"
    }
  },
  compoundVariants: [{ variant: "bordered", padding: "md", className: "bfrs-px-[22px] bfrs-py-[18px]" }],
  defaultVariants: {
    variant: "default",
    padding: "md"
  }
});

export const Card = forwardRef<HTMLDivElement, CardProps>(({ className, variant, padding, interactive, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ variant, padding, interactive }), className)} {...props} />
));

Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("bfrs-border-b bfrs-border-border bfrs-px-4 bfrs-py-3", className)} {...props} />
));

CardHeader.displayName = "CardHeader";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("bfrs-px-4 bfrs-py-4", className)} {...props} />
));

CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("bfrs-border-t bfrs-border-border bfrs-px-4 bfrs-py-3", className)} {...props} />
));

CardFooter.displayName = "CardFooter";
